// src/appCheckBootstrap.js
// LOOP APP CHECK NATIVE + LOCAL WEB DEBUG V4
//
// Android uses Play Integrity.
// iOS uses App Attest, configured natively before FirebaseApp.configure().
//
// Because Loop uses the Firebase JavaScript SDK inside the native WebView,
// the native App Check token is bridged into the JavaScript SDK through a
// Firebase CustomProvider.
//
// Web behavior:
// - Production uses reCAPTCHA Enterprise when its public site key is present.
// - Development on localhost uses Firebase's official App Check debug mode.
// - The debug token is never exposed by this module or committed to source.
// - The browser console prints the token once; register it in Firebase Console.
//
// Scope:
// - Android and iOS native runtimes.
// - Production web with reCAPTCHA Enterprise.
// - Localhost web development with the official debug provider.

import { Capacitor } from "@capacitor/core";
import { FirebaseAppCheck } from "@capacitor-firebase/app-check";
import {
  CustomProvider,
  ReCaptchaEnterpriseProvider,
  getToken as getWebAppCheckToken,
  initializeAppCheck,
} from "firebase/app-check";

const GLOBAL_STATE_KEY = "__LOOP_APP_CHECK_BOOTSTRAP_V4__";
const FALLBACK_TOKEN_TTL_MS = 55 * 60 * 1000;
const env = typeof process !== "undefined" ? process.env : {};

const DEFAULT_WEB_RECAPTCHA_ENTERPRISE_SITE_KEY =
  "6LcCBegsAAAAACIGuvX5kNCUulPGmJKoLolkzrzx";

const WEB_RECAPTCHA_ENTERPRISE_SITE_KEY = String(
  env.REACT_APP_FIREBASE_APPCHECK_RECAPTCHA_ENTERPRISE_SITE_KEY ||
    DEFAULT_WEB_RECAPTCHA_ENTERPRISE_SITE_KEY,
).trim();

const WEB_LOCAL_DEBUG_SETTING = String(
  env.REACT_APP_FIREBASE_APPCHECK_LOCAL_DEBUG || "auto",
).trim().toLowerCase();

function formatError(error) {
  if (!error) return "unknown-error";

  const code = error?.code ? String(error.code) : "";
  const message = error?.message ? String(error.message) : String(error);

  return `${code} ${message}`.trim();
}

function getPlatform() {
  try {
    return Capacitor.getPlatform();
  } catch (_) {
    return "unknown";
  }
}

function isNativeAppCheckRuntime() {
  try {
    const platform = Capacitor.getPlatform();

    return (
      Capacitor.isNativePlatform() &&
      (platform === "android" || platform === "ios")
    );
  } catch (_) {
    return false;
  }
}

function isLocalhostWebRuntime() {
  try {
    if (typeof window === "undefined") return false;

    const hostname = String(window.location?.hostname || "")
      .trim()
      .toLowerCase();

    return (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "::1" ||
      hostname === "[::1]"
    );
  } catch (_) {
    return false;
  }
}

function isDevelopmentBuild() {
  try {
    return String(env.NODE_ENV || "").toLowerCase() !== "production";
  } catch (_) {
    return false;
  }
}

function shouldUseLocalWebDebugMode() {
  if (isNativeAppCheckRuntime()) return false;
  if (!isDevelopmentBuild()) return false;
  if (!isLocalhostWebRuntime()) return false;

  if (["0", "false", "no", "off", "disabled"].includes(WEB_LOCAL_DEBUG_SETTING)) {
    return false;
  }

  return true;
}

function enableLocalWebDebugMode() {
  if (!shouldUseLocalWebDebugMode()) return false;

  try {
    const root = typeof globalThis !== "undefined" ? globalThis : null;
    if (!root) return false;

    if (
      root.FIREBASE_APPCHECK_DEBUG_TOKEN === undefined ||
      root.FIREBASE_APPCHECK_DEBUG_TOKEN === null ||
      root.FIREBASE_APPCHECK_DEBUG_TOKEN === ""
    ) {
      root.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }

    return true;
  } catch (_) {
    return false;
  }
}

function providerNameForPlatform(platform, debugMode = false) {
  if (platform === "android") return "Play Integrity";
  if (platform === "ios") return "App Attest";
  if (debugMode) return "Firebase App Check Debug";
  if (platform === "web" && WEB_RECAPTCHA_ENTERPRISE_SITE_KEY) {
    return "reCAPTCHA Enterprise";
  }
  return "disabled";
}

function publicStatus(state) {
  return {
    platform: state.platform,
    provider: state.provider,
    enabled: state.enabled,
    debugMode: state.debugMode,
    localhost: state.localhost,
    nativeInitialized: state.nativeInitialized,
    webInitialized: state.webInitialized,
    tokenAutoRefreshEnabled: state.tokenAutoRefreshEnabled,
    lastTokenAt: state.lastTokenAt,
    expireTimeMillis: state.expireTimeMillis,
    error: state.error,
  };
}

function exposeStatus(state) {
  try {
    if (typeof window !== "undefined") {
      // Diagnostics only. Never expose the App Check token.
      window.__LOOP_APP_CHECK_STATUS__ = () => publicStatus(state);
    }
  } catch (_) {}
}

function getOrCreateState() {
  const root = typeof globalThis !== "undefined" ? globalThis : {};

  if (root[GLOBAL_STATE_KEY]) {
    return root[GLOBAL_STATE_KEY];
  }

  const platform = getPlatform();
  const debugMode = shouldUseLocalWebDebugMode();
  const localhost = isLocalhostWebRuntime();

  const state = {
    platform,
    provider: providerNameForPlatform(platform, debugMode),
    enabled: false,
    debugMode,
    localhost,
    nativeInitialized: false,
    webInitialized: false,
    tokenAutoRefreshEnabled: false,
    lastTokenAt: null,
    expireTimeMillis: null,
    error: null,
    ready: null,
    nativeReady: null,
    webInstance: null,
  };

  root[GLOBAL_STATE_KEY] = state;
  exposeStatus(state);

  return state;
}

function makeAppCheckClientError(message, code, reason, originalError = null) {
  const error = new Error(message);
  error.code = code;
  error.details = {
    reason,
    provider: getOrCreateState().provider,
    debugMode: getOrCreateState().debugMode,
    localhost: getOrCreateState().localhost,
  };

  if (originalError) {
    error.customData = {
      originalCode: originalError?.code || null,
      originalMessage: originalError?.message || String(originalError),
    };
  }

  return error;
}

export function initializeLoopAppCheck(app) {
  const state = getOrCreateState();

  if (state.ready) {
    return state.ready;
  }

  if (!isNativeAppCheckRuntime()) {
    const debugModeEnabled = enableLocalWebDebugMode();
    state.debugMode = debugModeEnabled;
    state.localhost = isLocalhostWebRuntime();
    state.provider = providerNameForPlatform(state.platform, debugModeEnabled);

    if (!debugModeEnabled && !WEB_RECAPTCHA_ENTERPRISE_SITE_KEY) {
      state.enabled = false;
      state.provider = "disabled";
      state.error = "app-check/web-provider-not-configured";
      state.ready = Promise.resolve(publicStatus(state));
      exposeStatus(state);

      return state.ready;
    }

    state.enabled = true;
    state.error = null;

    try {
      // initializeAppCheck still expects a provider object even when the
      // official localhost debug token flow is enabled. In debug mode Firebase
      // exchanges FIREBASE_APPCHECK_DEBUG_TOKEN instead of using this
      // placeholder provider for attestation.
      const provider = debugModeEnabled
        ? new CustomProvider({
            getToken: async () => {
              throw makeAppCheckClientError(
                "El proveedor temporal de depuración no debe ejecutarse directamente.",
                "app-check/debug-provider-fallback-called",
                "debug-provider-fallback-called",
              );
            },
          })
        : new ReCaptchaEnterpriseProvider(
            WEB_RECAPTCHA_ENTERPRISE_SITE_KEY,
          );

      state.webInstance = initializeAppCheck(app, {
        provider,
        isTokenAutoRefreshEnabled: true,
      });
      state.webInitialized = true;
      state.tokenAutoRefreshEnabled = true;

      if (debugModeEnabled) {
        console.info(
          "[app-check] Local debug mode enabled. Register the App Check debug token printed by Firebase in the Firebase Console.",
        );
      }
    } catch (error) {
      state.enabled = false;
      state.webInitialized = false;
      state.tokenAutoRefreshEnabled = false;
      state.error = formatError(error);

      console.warn(
        `[app-check] ${state.provider} initialization failed:`,
        state.error,
      );
    }

    state.ready = Promise.resolve(publicStatus(state));
    exposeStatus(state);

    return state.ready;
  }

  state.enabled = true;
  state.error = null;

  // LOOP Android App Check debug gate:
  // - OFF by default, including normal/release builds.
  // - Enabled only when the React build is created with
  //   REACT_APP_FIREBASE_APPCHECK_ANDROID_DEBUG=true.
  // - iOS never uses this flag.
  const androidNativeDebugMode =
    state.platform === "android" &&
    ["1", "true", "yes", "on"].includes(
      String(
        env.REACT_APP_FIREBASE_APPCHECK_ANDROID_DEBUG || "",
      ).trim().toLowerCase(),
    );

  state.debugMode = androidNativeDebugMode;

  if (androidNativeDebugMode) {
    console.warn(
      "[app-check] ANDROID DEBUG PROVIDER ENABLED FOR THIS LOCAL TEST BUILD ONLY.",
    );
  }

  // On iOS, AppDelegate has already installed the App Attest provider before
  // FirebaseApp.configure(). On Android, debugToken=true switches only this
  // explicitly flagged test bundle to Firebase's debug provider.
  state.nativeReady = FirebaseAppCheck.initialize({
    debugToken: androidNativeDebugMode,
    isTokenAutoRefreshEnabled: true,
  });

  const provider = new CustomProvider({
    getToken: async () => {
      await state.nativeReady;

      const result = await FirebaseAppCheck.getToken({
        forceRefresh: false,
      });

      if (!result?.token) {
        throw makeAppCheckClientError(
          "No se recibió un token nativo de App Check.",
          "app-check/native-token-missing",
          "native-token-missing",
        );
      }

      const parsedExpireTime = Number(result.expireTimeMillis);

      const expireTimeMillis =
        Number.isFinite(parsedExpireTime) &&
        parsedExpireTime > Date.now()
          ? parsedExpireTime
          : Date.now() + FALLBACK_TOKEN_TTL_MS;

      state.lastTokenAt = new Date().toISOString();
      state.expireTimeMillis = expireTimeMillis;
      state.error = null;

      exposeStatus(state);

      return {
        token: result.token,
        expireTimeMillis,
      };
    },
  });

  // Register App Check before creating Auth, Firestore and Storage instances.
  try {
    state.webInstance = initializeAppCheck(app, {
      provider,
      isTokenAutoRefreshEnabled: true,
    });

    state.webInitialized = true;
  } catch (error) {
    state.error = formatError(error);
    exposeStatus(state);

    console.warn(
      "[app-check] Firebase JS initialization failed:",
      state.error,
    );
  }

  state.ready = state.nativeReady
    .then(async () => {
      state.nativeInitialized = true;

      try {
        await FirebaseAppCheck.setTokenAutoRefreshEnabled({
          enabled: true,
        });

        state.tokenAutoRefreshEnabled = true;
      } catch (error) {
        state.error = formatError(error);

        console.warn(
          "[app-check] Auto-refresh setup failed:",
          state.error,
        );
      }

      exposeStatus(state);

      console.info(
        `[app-check] ${state.provider} initialized`,
        publicStatus(state),
      );

      return publicStatus(state);
    })
    .catch((error) => {
      state.nativeInitialized = false;
      state.error = formatError(error);

      exposeStatus(state);

      console.warn(
        `[app-check] ${state.provider} initialization failed:`,
        state.error,
      );

      return publicStatus(state);
    });

  exposeStatus(state);

  return state.ready;
}

export async function getLoopAppCheckToken(forceRefresh = false) {
  const state = getOrCreateState();

  if (!state.enabled) {
    state.error = state.error || "app-check/provider-disabled";
    exposeStatus(state);
    return null;
  }

  if (state.ready) {
    await state.ready;
  }

  if (!state.webInitialized && !isNativeAppCheckRuntime()) {
    throw makeAppCheckClientError(
      "App Check todavía no está inicializado.",
      "app-check/provider-not-ready",
      "web-provider-not-ready",
    );
  }

  let result = null;

  try {
    if (isNativeAppCheckRuntime()) {
      result = await FirebaseAppCheck.getToken({
        forceRefresh: Boolean(forceRefresh),
      });
    } else if (state.webInstance) {
      result = await getWebAppCheckToken(
        state.webInstance,
        Boolean(forceRefresh),
      );
    } else {
      throw makeAppCheckClientError(
        "App Check todavía no está listo.",
        "app-check/provider-not-ready",
        "provider-not-ready",
      );
    }
  } catch (error) {
    state.error = formatError(error);
    exposeStatus(state);

    const raw = state.error.toLowerCase();
    const debugTokenRejected =
      state.debugMode &&
      (
        raw.includes("403") ||
        raw.includes("fetch-status-error") ||
        raw.includes("throttled") ||
        raw.includes("permission")
      );

    if (debugTokenRejected) {
      throw makeAppCheckClientError(
        "El token de depuración de App Check todavía no está autorizado.",
        "app-check/debug-token-not-registered",
        "debug-token-not-registered",
        error,
      );
    }

    throw error;
  }

  if (!result?.token) {
    throw makeAppCheckClientError(
      "App Check no devolvió un token.",
      "app-check/token-missing",
      "token-missing",
    );
  }

  state.lastTokenAt = new Date().toISOString();
  state.expireTimeMillis = Number(result.expireTimeMillis) || null;
  state.error = null;

  exposeStatus(state);

  return result.token;
}

export function getLoopAppCheckStatus() {
  return publicStatus(getOrCreateState());
}
